<template>
  <v-dialog :value="sectionEditor.show" :width="1200" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <h3 class="label-input">Section Name</h3>
          <v-text-field v-model="fields.title" label="Name" />
        </div>

        <div class="mb-6">
          <label class="label-input">Section Background</label>
          <div class="group-item">
            <v-row>
              <v-col cols="6">
                <v-checkbox v-model="fields.topPadding" label="Top Padding" />
              </v-col>
              <v-col cols="6">
                <v-checkbox v-model="fields.bottomPadding" label="Bottom Padding" />
              </v-col>
            </v-row>

            <v-row>
              <v-col md="12" cols="12">
                <div class="d-flex align-center gap-3">
                  <input
                    type="color"
                    name="backgroundColor"
                    style="width: 40px; height: 40px; margin-right: 12px"
                    :value="fields.colors.backgroundColor"
                    @change="handleChangeBackgroundColor"
                  />
                  <v-text-field v-model="fields.colors.backgroundColor" label="Background Color" />
                  <v-btn @click="handleResetBackgroundColor" small text color="primary"> Reset </v-btn>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-6">
          <label class="label-input mb-4 d-flex align-center gap-2" htmlFor="mediaShow" style="width: fit-content">
            <span>Media</span>
            <input class="ml-2 cursor-pointer" type="checkbox" v-model="fields['media'].show" id="mediaShow" />
          </label>
          <div class="group-item">
            <v-select v-model="fields.media.position" :items="positionOptions" label="Position" />
            <v-radio-group v-model="fields.media.type" row>
              <v-radio label="Video" value="video"></v-radio>
              <v-radio label="Image" value="image"></v-radio>
            </v-radio-group>
            <div v-if="fields.media.type === 'image'">
              <UploadImage
                ref="mediaImageUpload"
                @callback="handleUploadMediaImage"
                :image="{ imageSrc: fields.media.image }"
                :height="`200`"
                :typeUpload="'image'"
                @callbackDelete="handleRemoveMediaImage"
              />
            </div>
            <div v-if="fields.media.type === 'video'">
              <div class="d-flex align-center gap-6 mb-4">
                <v-text-field v-model="fields.media.video" label="Video URL" />
                <v-btn @click="handlePreviewVideo" text color="primary"> <v-icon>mdi-reload</v-icon> Reload </v-btn>
              </div>
              <div class="video-wrapper" v-if="fields.media.video">
                <div class="wrap-video">
                  <video width="100%" height="auto" autoplay muted loop playsinline :ref="'videoPlayer'">
                    <source data-attribute="card-video" type="video/mp4" :src="fields.media.video" />
                  </video>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-6">
          <h3 class="label-input">Main Content</h3>
          <ckeditor :editor="editor" v-model="fields.content" :config="editorConfig" />
        </div>

        <div class="mb-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3 class="label-input mb-0">Feature List ({{ fields.list.length }})</h3>
            <v-btn small color="primary" @click="handleAddListItem">
              <v-icon size="16" left>mdi-plus</v-icon>
              Add Feature
            </v-btn>
          </div>

          <div v-if="fields.list.length === 0" class="text-center pa-4 grey--text">
            No features added yet. Click "Add Feature" to get started.
          </div>

          <v-expansion-panels v-else accordion>
            <v-expansion-panel v-for="(item, i) in fields.list" :key="i">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center" style="width: 100%">
                  <div class="d-flex align-center">
                    <div class="feature-thumbnail mr-3">
                      <img v-if="item.image" :src="item.image" alt="" />
                      <v-icon v-else color="grey lighten-1">mdi-image</v-icon>
                    </div>
                    <span v-html="item.text.replace(/<[^>]*>/g, '').trim()"></span>
                  </div>
                  <v-btn icon small @click.stop="handleRemoveListItem(i)">
                    <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="pa-2">
                  <div class="mb-4">
                    <UploadImage
                      :ref="`listImageUpload${i}`"
                      @callback="obj => handleUploadListImage(obj, i)"
                      :image="{ imageSrc: item.image }"
                      :height="`100`"
                      :typeUpload="'image'"
                      @callbackDelete="() => handleRemoveListImage(i)"
                    />
                  </div>

                  <ckeditor :editor="editor" v-model="item.text" :config="editorConfig" />
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="mb-6">
          <h3 class="label-input">Footer</h3>
          <ckeditor :editor="editor" v-model="fields.footer" :config="editorConfig" />
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save Changes</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeepObject } from '../common/utils';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { replaceImageUrl } from '../common/utils';

export default {
  name: 'cardFeatureSectionEditor',
  components: {
    UploadImage,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        {
          text: 'Left',
          value: 'left',
        },
        {
          text: 'Right',
          value: 'right',
        },
      ],
      fields: {
        title: 'Card Feature Section',
        enableAnchor: false,
        media: {
          image: '',
          video: '',
          type: 'image', // image, video
          position: 'left',
          show: true,
        },
        content: '',
        list: [],
        footer: '',
        colors: {
          backgroundColor: '#ffffff',
        },
        topPadding: true,
        bottomPadding: true,
      },
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },

    handleSave() {
      const updatedSection = {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtmlFromData.sectionCardFeature(this.sectionEditor.section.html, this.fields),
      };
      this.$emit('update:section', updatedSection);
      this.handleClosePopup();
    },

    // Media image handlers
    handleUploadMediaImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.media.image = obj.imageSrc;
      }
    },

    handleRemoveMediaImage() {
      this.fields.media.image = '';
    },

    // List item handlers
    handleAddListItem() {
      this.fields.list.push({
        image: replaceImageUrl(
          'https://cdn.skyfastimg.com/images/2026/02/24/image-699d2c40c8755dd3bf7829a3.webp?w=50&h=50&q=80',
        ),
        text: 'Feature Text',
      });
    },

    handleRemoveListItem(index) {
      this.fields.list.splice(index, 1);
    },

    handleUploadListImage(obj, index) {
      if (obj && obj.imageSrc) {
        this.fields.list[index].image = obj.imageSrc;
      }
    },

    handleRemoveListImage(index) {
      this.fields.list[index].image = '';
    },

    handlePreviewVideo() {
      const video = this.$refs.videoPlayer;
      if (video) {
        video.load();
        video.play();
      }
    },

    handleChangeBackgroundColor(event) {
      this.fields.colors.backgroundColor = event.target.value;
    },
    handleResetBackgroundColor() {
      this.fields.colors.backgroundColor = '#ffffff';
    },
  },

  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = cloneDeepObject(this.sectionEditor.sectionExtract);
    }
  },

  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = cloneDeepObject(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
::v-deep .ck-editor__editable {
  min-height: 100px !important;
}

.label-input {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: block;
}

.header-modal,
.footer-modal {
  padding: 0 24px;
  border-bottom: 1px solid #e0e0e0;
}

.footer-modal {
  border-top: 1px solid #e0e0e0;
  border-bottom: none;
}

.d-flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.gap-3 {
  gap: 12px;
}

.feature-thumbnail {
  width: 80px;
  height: 50px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

::v-deep .v-text-field--outlined {
  .v-input__control {
    .v-input__slot {
      min-height: 40px;
    }
  }
}

::v-deep .v-textarea {
  &.v-text-field--outlined {
    .v-input__control {
      .v-input__slot {
        min-height: 80px;
      }
    }
  }
}
.video-wrapper {
  background: rgba(0, 0, 0, 0.767);
  .wrap-video {
    max-width: 300px;
    margin: 0 auto;
    video {
      display: block;
      width: 100%;
      height: auto;
    }
  }
}
</style>
